import React, { useState, useContext } from 'react';

// project-imports
import { GoogleAnalyticsEnum, pageView, triggerFunction } from '../../utils/google-analytics';
import SEO from '../../common/SEO';
import Layout from '../../common/Layout';
import FaqStencil from './FaqsStencil';
import BreadcrumbOne from '../../common/breadcrumb/BreadcrumbOne';

// third-party
import { Accordion } from 'react-bootstrap';
import { useAccordionButton } from 'react-bootstrap/AccordionButton';
import AccordionContext from 'react-bootstrap/AccordionContext';
import SectionTitle from '../../components/sectionTitle/SectionTitle';


// Constant variable
// Faqs data

const accordionItems = [
    {
        "id": 1,
        "title": "What is Salesforce and how is it used by businesses?",
        "body": "<p>Salesforce is the world’s leading cloud-based CRM platform that enables businesses to manage sales, marketing, service, analytics, and customer engagement from a single ecosystem. It helps organizations automate workflows, improve productivity, and gain insights into customer behavior. Companies across industries use Salesforce to drive growth and enhance customer satisfaction.</p>",
        "keywords": "What is Salesforce, Salesforce CRM, Salesforce for business, Salesforce benefits"
    },
    {
        "id": 2,
        "title": "What roles and job titles exist in the Salesforce ecosystem?",
        "body": "<p>The Salesforce ecosystem offers diverse career paths, including Salesforce Administrator, Developer, Consultant, Business Analyst, Architect, and Marketing Cloud Specialist. Each role contributes differently — from configuring systems and coding applications to designing architecture and implementing multi-cloud strategies. These roles are in high demand worldwide.</p>",
        "keywords": "Salesforce roles, Salesforce career options, Salesforce job titles, Salesforce jobs 2025"
    },
    {
        "id": 3,
        "title": "What are the most in-demand Salesforce skills in 2025?",
        "body": "<p>In 2025, companies are prioritizing professionals skilled in Apex programming, Lightning Web Components (LWC), Salesforce Flows, AI Cloud, and MuleSoft integrations. Data Cloud and automation skills are also in demand. Employers seek multi-cloud knowledge and practical expertise in delivering end-to-end solutions rather than just certifications.</p>",
        "keywords": "Salesforce skills 2025, Apex, Lightning Web Components, Salesforce Flows, MuleSoft"
    },
    {
        "id": 4,
        "title": "Do I need certifications to build a career in Salesforce?",
        "body": "<p>Yes. Salesforce certifications validate your expertise and boost your employability. Foundational credentials like Salesforce Certified Administrator and Platform Developer I are great starting points. Advanced certifications like Technical Architect or Data Cloud Consultant demonstrate specialization and can significantly increase your salary potential.</p>",
        "keywords": "Salesforce certification, Salesforce Admin certification, Salesforce Developer certification"
    },
    {
        "id": 5,
        "title": "What is the career outlook for Salesforce professionals in 2025?",
        "body": "<p>Salesforce continues to dominate the CRM space, driving a steady demand for professionals across roles. The career outlook remains strong, especially for those who adapt to AI, automation, and data analytics. Specialized roles like Salesforce Architect, Integration Expert, and Data Cloud Consultant are particularly high-growth areas.</p>",
        "keywords": "Salesforce career growth, Salesforce jobs 2025, Salesforce demand 2025"
    },
    {
        "id": 6,
        "title": "How does Salesforce compare to other CRM platforms?",
        "body": "<p>Salesforce stands out from competitors like HubSpot, Zoho, and Microsoft Dynamics due to its scalability, AppExchange marketplace, and ecosystem integration. It supports deep customization, AI-driven insights, and robust security, making it ideal for enterprises that want an end-to-end digital transformation platform.</p>",
        "keywords": "Salesforce vs HubSpot, Salesforce vs Zoho, Salesforce comparison, Salesforce CRM advantages"
    },
    {
        "id": 7,
        "title": "Can non-technical professionals succeed in the Salesforce ecosystem?",
        "body": "<p>Absolutely. Non-technical professionals can start as Salesforce Administrators, Business Analysts, or Functional Consultants. These roles focus on configuration, data management, and process optimization rather than coding. Over time, you can transition into technical or architectural roles through learning and certification.</p>",
        "keywords": "Salesforce for beginners, Salesforce without coding, non-technical Salesforce careers"
    },
    {
        "id": 8,
        "title": "What skills help a Salesforce Administrator stand out in 2025?",
        "body": "<p>In 2025, a standout Salesforce Administrator understands Flows automation, permission management, data modeling, and security best practices. Knowledge of multiple clouds (Sales, Service, and Marketing), AI setup, and reporting tools like Tableau CRM enhances an Admin’s career growth and job opportunities.</p>",
        "keywords": "Salesforce Administrator skills, Salesforce Flows, Salesforce Admin 2025, Salesforce automation"
    },
    {
        "id": 9,
        "title": "What development skills are important for Salesforce Developers in 2025?",
        "body": "<p>Developers should master Apex, Lightning Web Components (LWC), SOQL, integrations (REST/SOAP APIs), and version control. Familiarity with DevOps tools like Copado or Gearset and understanding scalable design patterns like fflib are also crucial for staying relevant in the modern Salesforce ecosystem.</p>",
        "keywords": "Salesforce Developer skills, Apex coding, LWC, Salesforce integration, Salesforce DevOps"
    },
    {
        "id": 10,
        "title": "How is artificial intelligence (AI) affecting Salesforce careers?",
        "body": "<p>AI is transforming Salesforce with tools like Einstein GPT and AI Cloud. Professionals skilled in using AI for analytics, lead scoring, and customer engagement are in high demand. Salesforce experts now need to combine CRM expertise with data science and prompt engineering for maximum impact.</p>",
        "keywords": "Salesforce AI, Einstein GPT, Salesforce AI Cloud, AI in CRM"
    },
    {
        "id": 11,
        "title": "Is remote work common for Salesforce professionals in 2025?",
        "body": "<p>Yes, remote work is now the norm in the Salesforce industry. Many companies operate globally and hire certified Salesforce professionals regardless of geography. However, global hiring increases competition, making skill diversification and certification essential for career stability.</p>",
        "keywords": "Salesforce remote jobs, Salesforce work from home, Salesforce hybrid work"
    },
    {
        "id": 12,
        "title": "What is the role of domain specialization in Salesforce careers?",
        "body": "<p>Domain expertise in industries like Finance, Healthcare, Education, or Retail boosts your value. Salesforce offers industry-specific clouds such as Financial Services Cloud, Health Cloud, and Education Cloud. Understanding domain workflows helps deliver better solutions and makes you a more desirable hire.</p>",
        "keywords": "Salesforce industry clouds, Financial Services Cloud, Health Cloud, Salesforce domain specialization"
    },
    {
        "id": 13,
        "title": "How important are soft skills in Salesforce careers?",
        "body": "<p>Soft skills are critical. Employers value communication, problem-solving, project management, and stakeholder collaboration as much as technical skills. Salesforce professionals often work directly with clients, making interpersonal and presentation skills key to long-term success.</p>",
        "keywords": "Salesforce soft skills, Salesforce communication skills, Salesforce consulting skills"
    },
    {
        "id": 14,
        "title": "What are the expected salary ranges for Salesforce professionals in 2025?",
        "body": "<p>In 2025, Salesforce Administrators in India can earn ₹6–12 LPA, Developers ₹8–18 LPA, and Architects ₹25+ LPA. In the U.S., salaries range from $85,000 for Administrators to over $160,000 for Architects. Certifications, experience, and domain expertise greatly influence earnings.</p>",
        "keywords": "Salesforce salary 2025, Salesforce pay scale, Salesforce India salary, Salesforce Architect salary"
    },
    {
        "id": 15,
        "title": "What are the biggest challenges for new Salesforce learners in 2025?",
        "body": "<p>New learners face challenges like keeping up with Salesforce’s frequent updates, understanding real-world use cases, and building hands-on experience. Starting with Trailhead, earning foundational certifications, and joining community projects can help overcome these obstacles.</p>",
        "keywords": "Salesforce learning path, Salesforce challenges, Salesforce Trailhead, Salesforce training 2025"
    },
    {
        "id": 16,
        "title": "How does the Salesforce certification path look in 2025?",
        "body": "<p>The certification path starts with Salesforce Administrator and App Builder, progressing to Developer and Architect levels. New certifications like AI Associate, Data Cloud Consultant, and Integration Architect reflect Salesforce’s shift toward automation, AI, and multi-cloud solutions.</p>",
        "keywords": "Salesforce certification path, Salesforce AI Associate, Salesforce Data Cloud Consultant"
    },
    {
        "id": 17,
        "title": "What is the future outlook for careers in Salesforce?",
        "body": "<p>The future is bright. As more enterprises move to cloud-based operations and automation, Salesforce remains at the center of digital transformation. Demand for skilled Salesforce professionals will continue to rise, especially in AI, data integration, and automation roles.</p>",
        "keywords": "Salesforce career future, Salesforce job growth, Salesforce future trends"
    },
    {
        "id": 18,
        "title": "Should I start with Salesforce Admin or Developer path?",
        "body": "<p>If you enjoy business processes and system configuration, start as an Administrator. If you like problem-solving through code, choose the Developer path. Both paths can lead to high-paying roles like Solution Architect or Consultant as you gain multi-cloud expertise.</p>",
        "keywords": "Salesforce Admin vs Developer, Salesforce career path, Salesforce learning roadmap"
    },
    {
        "id": 19,
        "title": "How important are integrations and APIs for Salesforce professionals?",
        "body": "<p>Integrations and APIs are essential as Salesforce rarely operates alone. Professionals must know how to use REST, SOAP, and Streaming APIs, along with MuleSoft or external connectors, to ensure smooth data exchange between Salesforce and other systems.</p>",
        "keywords": "Salesforce API, Salesforce integration, MuleSoft, REST API Salesforce"
    },
    {
        "id": 20,
        "title": "What is the role of data security and compliance in Salesforce?",
        "body": "<p>Data security is at the heart of Salesforce. Professionals must implement strong access controls, encryption, and field-level security while ensuring compliance with regulations like GDPR. Secure design is a top priority in every Salesforce implementation.</p>",
        "keywords": "Salesforce data security, Salesforce compliance, Salesforce GDPR, Salesforce encryption"
    }
];

const CustomToggle = ({ children, eventKey }) => {

    const { activeEventKey } = useContext(AccordionContext);
    const decoratedOnClick = useAccordionButton(eventKey);
    const isCurrentEventKey = activeEventKey === eventKey;
    return <button type="button" onClick={() => { decoratedOnClick(); triggerFunction(GoogleAnalyticsEnum.BUTTON, GoogleAnalyticsEnum.ALL_FAQS_PAGE_ACCORDIAN_BUTTON) }} aria-expanded={isCurrentEventKey ? true : false} className="edu-accordion-button">{children}</button>// return <button type="button" onClick={decoratedOnClick} aria-expanded={isCurrentEventKey ? true : false} className="edu-accordion-button">{children}</button>
}

const Faq = (props) => {

    // State variable intialization
    const [stencilLoading, setStencilLoading] = useState(true);
    const [activeId, setActiveId] = useState('0');

    // Loading timeout
    setTimeout(() => {
        setStencilLoading(false);
    }, 1000);

    // Constant function
    function toggleActive(id) {
        try {
            if (activeId === id) {
                setActiveId(null);
            } else {
                setActiveId(id);
            }
        } catch (error) {
            console.log("Error while toggling faqs", JSON.stringify(error && error.message));
        }
    }

    if (stencilLoading) {
        return <FaqStencil />; // Loading stencil
    }
    return (
        <>
            {pageView(window.location.pathname, GoogleAnalyticsEnum.FAQs)}

            <SEO title="FAQs | Longswitch Academy" url={process.env.PUBLIC_URL + `/faqs`} /> {/* Set SEO title */}
            <Layout searchDisable={true}>
                <BreadcrumbOne
                    title="All FAQs" // Breadcrumb title 
                    rootUrl="/"
                    parentUrl="Home"
                    googleEnum={GoogleAnalyticsEnum.ALL_FAQS_TO_HOME}
                    currentUrl="All FAQs"

                />
                <div className="edu-course-area ptb--60 bg-color-white">
                    <div className="container eduvibe-animated-shape mb--130">
                        <SectionTitle
                            classes="text-center"
                            slogan="How can we help?"
                            title="Frequently Asked Questions"
                        />
                        <div className="row y-gap-30 pt--50 text-center">
                            <div className="col-xl-8 offset-xl-2 mt-0">
                                <Accordion bsPrefix="landing-demo-faq edu-accordion-02 variation-2 landing-page-accordion bg-color-transparent" defaultActiveKey="0" flush>
                                    {accordionItems && accordionItems.length &&
                                        accordionItems.map((accordion, i) => (
                                            <Accordion.Item eventKey={i.toString()} key={i} onClick={() => toggleActive(i.toString())} bsPrefix={`edu-accordion-item ${activeId === i.toString() ? 'bg-active' : ''}`}>
                                                <div className="edu-accordion-header">
                                                    <CustomToggle eventKey={i.toString()}>{accordion.title}</CustomToggle>
                                                </div>
                                                <Accordion.Body bsPrefix="edu-accordion-body"><div dangerouslySetInnerHTML={{ __html: accordion.body }}></div></Accordion.Body>
                                            </Accordion.Item>
                                        ))
                                    }
                                </Accordion>
                            </div>
                        </div>
                    </div>
                </div>

            </Layout>
        </>
    )
}

export default Faq;